import { UsersService, type AuthorizationContext } from "@/generated";
import { getActiveGroupId } from "@/utils/activeGroup";

type Part = keyof AuthorizationContext;

type FirstLoad = {
  groupId: string;
  context: Promise<AuthorizationContext | null>;
  handed: Set<Part>;
};

// A page's first load: one request answers what the context routes answer,
// and each part goes to the first read of its key. Every later read, and any
// part the context could not give, goes to the key's own route as before.
let firstLoad: FirstLoad | null = null;

function start(): FirstLoad {
  const groupId = getActiveGroupId();
  // A response the client's interceptors swallowed (a signed-out visitor's
  // 401) resolves to undefined: the keys then read their own routes.
  const context = UsersService.getAuthorizationContext({
    query: { group_id: groupId || null },
  })
    .then((response) => response?.data ?? null)
    .catch(() => null);
  return { groupId, context, handed: new Set() };
}

/**
 * A part of the authorization context, once. Undefined when the part was handed
 * out already, the load failed, the context refused the part, or it was loaded
 * for another workspace than `groupId`: the caller then reads its own route.
 */
export async function fromContext<K extends Part>(
  part: K,
  groupId?: string
): Promise<NonNullable<AuthorizationContext[K]> | undefined> {
  firstLoad ??= start();
  if (firstLoad.handed.has(part)) return undefined;
  if (groupId !== undefined && groupId !== firstLoad.groupId) return undefined;
  firstLoad.handed.add(part);
  const value = (await firstLoad.context)?.[part];
  return value ?? undefined;
}

/** A key's first read from the context, every other one from its own route. */
export async function contextOr<K extends Part, R extends { data: unknown }>(
  part: K,
  groupId: string | undefined,
  own: () => Promise<R>
): Promise<R | { data: NonNullable<AuthorizationContext[K]> }> {
  const value = await fromContext(part, groupId);
  return value === undefined ? own() : { data: value };
}

export function resetContextLoad(): void {
  firstLoad = null;
}
